import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, ArrowUpRight } from "lucide-react";

const LINKS = [
  { to: "/individuals", label: "Individuals" },
  { to: "/employers", label: "Employers" },
  { to: "/about", label: "About" },
  { to: "/education", label: "Education" },
  { to: "/faq", label: "FAQ" },
  { to: "/contact", label: "Contact" },
];

export const Wordmark = ({ dark = false }) => (
  <span className="flex items-center gap-3">
    <img
      src={dark ? "/logo-cream.png" : "/logo-forest.png"}
      alt="OneHarvestCo. logo"
      className="h-9 w-9"
      data-testid={dark ? "footer-logo" : "nav-logo"}
    />
    <span className={`font-serif text-2xl font-medium tracking-tight ${dark ? "text-cream" : "text-forest"}`}>
      OneHarvest<span className="text-gold">Co.</span>
      <span className={`ml-2 align-middle font-mono text-[10px] uppercase tracking-[0.3em] ${dark ? "text-cream/60" : "text-muted-foreground"}`}>
        Benefits
      </span>
    </span>
  </span>
);

export const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => setOpen(false), [location.pathname]);

  return (
    <header
      className={`fixed inset-x-0 top-0 z-50 transition-all duration-500 ${
        scrolled ? "border-b border-border/60 bg-cream/75 backdrop-blur-xl" : "bg-transparent"
      }`}
      data-testid="site-navbar"
    >
      <div className="mx-auto flex max-w-7xl items-center justify-between px-6 py-4 lg:px-10">
        <Link to="/" data-testid="nav-logo-link" aria-label="OneHarvestCo. Benefits home">
          <Wordmark />
        </Link>

        <nav className="hidden items-center gap-8 lg:flex" data-testid="nav-desktop-menu">
          {LINKS.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              data-testid={`nav-link-${l.label.toLowerCase()}`}
              className={({ isActive }) =>
                `link-underline text-sm tracking-wide transition-colors duration-300 ${
                  isActive ? "text-terra" : "text-ink/80 hover:text-forest"
                }`
              }
            >
              {l.label}
            </NavLink>
          ))}
          <Link
            to="/contact?type=consultation"
            data-testid="nav-consultation-cta"
            className="group flex items-center gap-2 rounded-full bg-forest px-6 py-2.5 text-sm font-medium text-cream transition-all duration-300 hover:-translate-y-0.5 hover:bg-moss"
          >
            Request a Consultation
            <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
          </Link>
        </nav>

        <button
          className="text-forest lg:hidden"
          onClick={() => setOpen(!open)}
          aria-label="Toggle menu"
          data-testid="nav-mobile-toggle"
        >
          {open ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
        </button>
      </div>

      <AnimatePresence>
        {open && (
          <motion.nav
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
            className="overflow-hidden border-b border-border bg-cream/95 backdrop-blur-xl lg:hidden"
            data-testid="nav-mobile-menu"
          >
            <div className="flex flex-col gap-1 px-6 py-6">
              {LINKS.map((l, i) => (
                <motion.div
                  key={l.to}
                  initial={{ opacity: 0, x: -14 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.05 * i }}
                >
                  <NavLink
                    to={l.to}
                    data-testid={`nav-mobile-link-${l.label.toLowerCase()}`}
                    className="block py-2.5 font-serif text-2xl font-light text-forest"
                  >
                    {l.label}
                  </NavLink>
                </motion.div>
              ))}
              <Link
                to="/contact?type=consultation"
                data-testid="nav-mobile-consultation-cta"
                className="mt-4 inline-flex w-fit items-center gap-2 rounded-full bg-forest px-6 py-3 text-sm font-medium text-cream"
              >
                Request a Consultation
                <ArrowUpRight className="h-4 w-4" />
              </Link>
            </div>
          </motion.nav>
        )}
      </AnimatePresence>
    </header>
  );
};
